import { reportError } from '@/services/error-reporting'

interface ApiErrorReason {
  errors: { code: string; message?: string }[]
  statusCode?: number
  stack?: unknown
}

const UNHANDLED_REJECTION_TAG = 'unhandled-promise-rejection'

let droppedRejections = 0

export const getDroppedRejectionCount = (): number => droppedRejections

const isApiErrorReason = (reason: unknown): reason is ApiErrorReason => {
  if (typeof reason !== 'object' || reason === null) return false

  const errors = (reason as { errors?: unknown }).errors
  return Array.isArray(errors) && errors.length > 0 && typeof errors[0]?.code === 'string'
}

const stringOrUndefined = (value: unknown): string | undefined =>
  typeof value === 'string' && value.length > 0 ? value : undefined

const hasPayload = (reason: unknown): boolean => {
  if (typeof reason === 'string') return reason.length > 0
  if (typeof reason !== 'object' || reason === null) return false

  const { message, stack } = reason as { message?: unknown; stack?: unknown }
  return Boolean(stringOrUndefined(message) || stringOrUndefined(stack))
}

const captureUnhandledRejection = () => {
  window.onunhandledrejection = (event: PromiseRejectionEvent) => {
    const reason: unknown = event.reason
    const baseContext = { currentUrl: window.location.href, timestamp: new Date().toISOString() }

    if (reason instanceof Error) {
      reportError(reason, {
        name: reason.name,
        context: { ...baseContext, errorMessage: reason.message, errorStack: reason.stack },
        tags: UNHANDLED_REJECTION_TAG
      })
    } else if (isApiErrorReason(reason)) {
      const [first] = reason.errors
      const errorMessage = stringOrUndefined(first.message) ?? first.code
      const errorStack = stringOrUndefined(reason.stack)

      reportError(errorMessage, {
        name: `UnhandledRejection:${first.code}`,
        stack: errorStack,
        context: {
          ...baseContext,
          errorMessage,
          errorStack,
          // No stack of its own: the notice backtrace is our reporter, not the origin.
          stackSynthesized: errorStack === undefined,
          statusCode: reason.statusCode,
          errors: reason.errors
        },
        tags: UNHANDLED_REJECTION_TAG
      })
    } else if (hasPayload(reason)) {
      const { message, stack } =
        typeof reason === 'string'
          ? { message: reason, stack: undefined }
          : (reason as { message?: unknown; stack?: unknown })
      const errorMessage = stringOrUndefined(message) ?? 'Unhandled rejection'
      const errorStack = stringOrUndefined(stack)

      reportError(errorMessage, {
        name: 'UnhandledRejection',
        stack: errorStack,
        context: {
          ...baseContext,
          errorMessage,
          errorStack,
          // No stack of its own: the notice backtrace is our reporter, not the origin.
          stackSynthesized: errorStack === undefined
        },
        tags: UNHANDLED_REJECTION_TAG
      })
    } else {
      droppedRejections += 1

      if (import.meta.env.DEV) {
        console.warn(`Dropped ${droppedRejections} payload-less unhandled rejection(s)`)
      }
    }

    event.preventDefault()
  }
}

export { captureUnhandledRejection }
